'use client'

import React, { useMemo } from 'react'
import { QRCode } from '@ttsalpha/qrcode'

import { cn } from '@/lib/utils'
import { brandVars } from '@/lib/color/brand'
import { AA_TEXT, ensureContrast } from '@/lib/color/contrast'
import { DEFAULT_QR_DESIGN, buildQrProps, type QrDesign } from '@/lib/qr/design'
import { useQrLogo, type QrLogoArt } from '@/lib/qr/useQrLogo'
import NfcWaves from '@/components/livecontact/NfcWaves'
import Skeleton from '@/components/livecontact/Skeleton'

/**
 * The QR anatomy of the design system: a white card with a hairline and a generous even gutter
 * holding the code, an optional logo on a quiet ring in the centre, and a "Resolves to <link>"
 * caption underneath.
 *
 * The code itself is pure SVG and takes the full per-card design — dot pattern, corner frames
 * and centres, colours, logo — through `design`. The card stays paper white in every theme:
 * cameras need the contrast, and the print/export surfaces render onto white stock.
 */

/** Hairline of the card — Figma "Iron". */
const CARD_LINE = '#E4E4E7'
/** Caption ink on paper, where theme tokens must not flip. */
const PAPER_MUTED = '#52525C'
const PAPER_LINK = '#316FFF'
/** Print and export surfaces render onto white stock, whatever the app's theme is. */
const PAPER_WHITE = '#FFFFFF'

export type QrCardProps = {
  /** Encoded value. Empty renders a skeleton in the code's place. */
  value?: string
  /** Code edge in px. Padding, radii and the caption gap scale off it. */
  size?: number
  /** Card gutter. Defaults to the design's proportion (~7.8% of the code). */
  padding?: number
  radius?: number
  codeRadius?: number
  /** Per-card design. Unset fields fall back to the classic black-on-white square code. */
  design?: Partial<QrDesign>
  /** Shorthands for the most common overrides; `design` wins where both are set. */
  fgColor?: string
  bgColor?: string
  marginSize?: number
  /**
   * Centre logo: art resolved from a design (`resolveQrLogoArt`), or a plain photo URL that is
   * cropped into a rounded tile filled with `logoTone`.
   */
  logo?: QrLogoArt | string | null
  logoTone?: string | null
  /** Muted lead-in of the caption. */
  captionPrefix?: string
  /** The short link. Without it the caption row is not rendered. */
  caption?: string
  /** Overrides the brand colour of the link — used on paper. */
  linkColor?: string
  /** Fixed inks instead of theme tokens, for print and export surfaces. */
  paper?: boolean
  /** Contactless mark above the card. Off for printed sheets and anything overlapping the top. */
  nfc?: boolean
  /** Turns the card into a button (the tile keeps its copy-on-click behaviour). */
  onClick?: () => void
  /** Accessible name of that button. */
  label?: string
  className?: string
  cardClassName?: string
  codeClassName?: string
  captionClassName?: string
}

const withoutUndefined = <T extends object>(value: T) =>
  Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as Partial<T>

const QrCard = ({
  value,
  size = 160,
  padding,
  radius,
  codeRadius,
  design,
  fgColor,
  bgColor,
  marginSize,
  logo,
  logoTone,
  captionPrefix = 'Resolves to',
  caption,
  linkColor,
  paper = false,
  nfc = true,
  onClick,
  label,
  className,
  cardClassName,
  codeClassName,
  captionClassName,
}: QrCardProps) => {
  const gutter = padding ?? Math.max(10, Math.round(size * 0.078))
  const cardRadius = radius ?? Math.max(12, Math.round(size * 0.05))
  const matrixRadius = codeRadius ?? Math.max(8, Math.round(size * 0.031))
  const gap = Math.max(12, Math.round(size * 0.075))

  const resolved = useMemo<QrDesign>(
    () => ({
      ...DEFAULT_QR_DESIGN,
      ...withoutUndefined({
        foregroundColor: fgColor,
        backgroundColor: bgColor,
        marginSize,
        logoBackground: typeof logo === 'string' ? logoTone || PAPER_WHITE : undefined,
      }),
      ...withoutUndefined(design || {}),
    }),
    [design, fgColor, bgColor, marginSize, logo, logoTone]
  )

  const art = useMemo<QrLogoArt | null>(() => {
    if (!logo) return null
    return typeof logo === 'string' ? { src: logo, fit: 'cover', inset: 0 } : logo
  }, [logo])
  const logoSrc = useQrLogo(art, resolved)

  const qrProps = useMemo(
    () =>
      buildQrProps(resolved, {
        value: value || '',
        size,
        logoSrc,
        className: cn('block', codeClassName),
        style: { borderRadius: matrixRadius },
        ariaLabel: caption ? `QR code for ${caption}` : undefined,
      }),
    [resolved, value, size, logoSrc, codeClassName, matrixRadius, caption]
  )

  const CardTag = onClick ? 'button' : 'div'

  return (
    <div className={cn('lc-asset-preview flex flex-col items-center', className)} style={{ gap }}>
      {nfc && (
        <span
          className={cn(
            'inline-flex items-center gap-[3px]',
            'text-[16px] font-bold uppercase leading-none tracking-[-0.01em]',
            !paper && 'text-brand'
          )}
          style={paper ? { color: PAPER_LINK } : undefined}
        >
          NFC
          <NfcWaves size={Math.max(21, Math.min(26, Math.round(size * 0.105)))} />
        </span>
      )}

      <CardTag
        {...(onClick
          ? { type: 'button' as const, onClick, title: label, 'aria-label': label }
          : {})}
        className={cn(
          'inline-flex shrink-0 items-center justify-center border border-solid bg-white outline-none',
          'shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]',
          onClick &&
            'transition-[transform,box-shadow] duration-[160ms] ease-out-strong hover:shadow-[0_4px_10px_-2px_rgba(0,0,0,0.14),0_2px_4px_-2px_rgba(0,0,0,0.1)] focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface active:scale-[0.98]',
          cardClassName
        )}
        style={{ padding: gutter, borderRadius: cardRadius, borderColor: CARD_LINE }}
      >
        {value ? (
          <QRCode {...qrProps} />
        ) : (
          <Skeleton style={{ width: size, height: size, borderRadius: matrixRadius }} />
        )}
      </CardTag>

      {caption && (
        <p
          className={cn(
            'max-w-full break-words text-center text-[14px] leading-[20px] tracking-[-0.2px]',
            paper ? '' : 'text-ink-4',
            captionClassName
          )}
          style={paper ? { color: PAPER_MUTED } : undefined}
        >
          {captionPrefix ? `${captionPrefix} ` : ''}
          <span
            className={cn(
              'font-medium',
              !paper && !linkColor && 'text-brand',
              linkColor && 'lc-brand'
            )}
            style={
              linkColor
                ? paper
                  ? // Print and export always land on white stock, so the correction is fixed.
                    { color: ensureContrast(linkColor, PAPER_WHITE, AA_TEXT) }
                  : { ...brandVars(linkColor), color: 'var(--brand-ink, var(--brand))' }
                : paper
                  ? { color: PAPER_LINK }
                  : undefined
            }
          >
            {caption}
          </span>
        </p>
      )}
    </div>
  )
}

export default React.memo(QrCard)

/** `https://livecontact.com/acme/` → `livecontact.com/acme` — the caption form in the design. */
export const shortenQrLink = (link?: string) =>
  (link || '').replace(/^https?:\/\//i, '').replace(/\/+$/, '')
